import useAppStore from "@/hooks/useAppStore";
import useSelectionManager from "@/hooks/useSelectionManager";
import { MODEL_TRANSFER_PHASE, MODEL_TRANSFER_STATUS } from "@/types/modelTransfer";
import i18n from "@i18n/i18n";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { GridLoadingOverlayProps } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import React from "react";

// Local types and defaults (utils removed)
export interface LoadingTask {
  id: string;
  label: string;
  status: "pending" | "loading" | "complete" | "error";
  progress?: number;
  progressLabel?: string;
  error?: string;
}

export interface MultiModelProgress {
  models: any[];
  totalModels: number;
  completedModels: number;
  overallPercent: number;
  totalBytesDownloaded: number;
  totalBytesExpected: number;
  phase: "idle" | "downloading" | "building" | "complete" | "error";
}

/** Returns default loading tasks for single model loading. Call at runtime to ensure i18n is initialized. */
export const getDefaultSingleModelTasks = (): LoadingTask[] => [
  { id: "fetch", label: i18n.t("loading-overlay.fetchingModelData"), status: "loading" },
  { id: "tree", label: i18n.t("loading-overlay.buildingTree"), status: "pending" },
];

// Types and helpers moved to ./LoadingOverlay.utils

/**
 * Props for the custom loading overlay component.
 * Extends GridLoadingOverlayProps to be compatible with MUI DataGrid slots.
 */
export interface LoadingOverlayProps extends Partial<GridLoadingOverlayProps> {
  /** Array of loading tasks to display */
  tasks?: LoadingTask[];
  /** Main title for the overlay */
  title?: string;
  /** Custom variant for our overlay */
  customVariant?: "minimal" | "linear" | "tasks";
  /** Multi-model download progress (for showing aggregate progress) */
  multiModelProgress?: MultiModelProgress | null;
}

// Default tasks moved to ./LoadingOverlay.utils

/**
 * Tree building progress (from TreeBuilderWorker).
 */
export interface TreeBuildProgressInfo {
  /** Current phase: 'loading', 'deserializing', 'grouping', 'flattening' */
  phase: "loading" | "deserializing" | "grouping" | "flattening";
  /** Progress percentage (0-100) */
  percent: number;
  /** Number of items processed */
  processedCount: number;
  /** Total items to process (estimated) */
  totalCount: number;
}

/**
 * Options for createLoadingTasks.
 */
export interface CreateLoadingTasksOptions {
  isFetching?: boolean;
  isFreshFetch?: boolean;
  isTreeBuilding?: boolean;
  isMetadataLoading?: boolean;
  fetchComplete?: boolean;
  treeComplete?: boolean;
  metadataComplete?: boolean;
  modelName?: string;
  /** Download progress for showing percentage and bytes */
  downloadProgress?: {
    percent: number;
    phase: string;
    bytesDownloaded: number;
    totalBytes: number;
  } | null;
  /** Multi-model progress for parallel downloads */
  multiModelProgress?: MultiModelProgress | null;
  /** Tree building progress from worker */
  treeBuildProgress?: TreeBuildProgressInfo | null;
  /** Label for the optional metadata-loading step */
  metadataLabel?: string;
}

/**
 * Helper to create loading tasks from loading state flags.
 */
export const createLoadingTasks = (options: CreateLoadingTasksOptions): LoadingTask[] => {
  const {
    isFetching = false,
    isFreshFetch = false,
    isTreeBuilding = false,
    isMetadataLoading = false,
    fetchComplete = false,
    treeComplete = false,
    metadataComplete = false,
    modelName,
    downloadProgress,
    multiModelProgress,
    treeBuildProgress,
    metadataLabel,
  } = options;

  console.log("[createLoadingTasks]", {
    isFetching,
    isFreshFetch,
    isTreeBuilding,
    fetchComplete,
    treeComplete,
    downloadProgress,
    treeBuildProgress,
  });

  const tasks: LoadingTask[] = [];

  // Check for multi-model download scenario
  const isMultiModel = multiModelProgress && multiModelProgress.totalModels > 1;

  // Phase 1: Fetching from backend API or loading from cache
  let fetchLabel: string;
  let fetchProgress: number | undefined;
  let fetchProgressLabel: string | undefined;

  // Check if we have active download progress (any phase except 'complete' and 'cached')
  const isActiveDownload = downloadProgress && ["checking", "downloading", "writing"].includes(downloadProgress.phase);

  // Check if download just completed - we'll now load from cache
  const isDownloadComplete = downloadProgress && ["complete", "cached"].includes(downloadProgress.phase);

  if (isMultiModel) {
    // Multi-model: show aggregate progress
    // Only rely on actual completed count, not phase (which can be unreliable during state transitions)
    const { completedModels, totalModels, overallPercent } = multiModelProgress!;
    if (completedModels >= totalModels && totalModels > 0) {
      // All downloads done - about to build tree
      fetchLabel = `All ${totalModels} models ready`;
    } else {
      fetchLabel = `Downloading models (${completedModels}/${totalModels})`;
      fetchProgress = overallPercent;
    }
  } else if (isActiveDownload) {
    // Single model with active download progress
    const { phase, percent } = downloadProgress!;

    // Set label based on phase
    if (phase === "checking") {
      fetchLabel = modelName ? `Checking "${modelName}"...` : "Checking cache...";
    } else if (phase === "writing") {
      fetchLabel = modelName ? `Saving "${modelName}"...` : "Saving to cache...";
    } else {
      fetchLabel = modelName ? `Downloading "${modelName}"` : "Downloading model data";
    }

    // Set progress (simplified - percentage only when available)
    if (phase === "downloading") {
      fetchProgress = percent;
    }
  } else if (isDownloadComplete) {
    // Download finished or found in cache - tree builder will load it
    const isCached = downloadProgress?.phase === "cached";
    if (isCached) {
      fetchLabel = modelName ? `"${modelName}" ready (cached)` : "Model ready (cached)";
    } else {
      fetchLabel = modelName ? `"${modelName}" ready` : "Download complete";
    }
  } else if (modelName) {
    fetchLabel = isFreshFetch ? `Fetching "${modelName}"` : `"${modelName}" ready (cached)`;
  } else {
    fetchLabel = isFreshFetch ? "Fetching from server" : "Model ready (cached)";
  }

  // Determine fetch status:
  // - complete if tree is complete (data is in grid) OR if fetchComplete AND (tree building or complete)
  // - loading if actively fetching OR waiting for tree to start (data propagating through React)
  // - pending otherwise
  let fetchStatus: LoadingTask["status"];
  if (treeComplete) {
    // Data is in the grid; treat fetch as complete so overlay can hide (avoids stuck overlay when DownloadManager state lags)
    fetchStatus = "complete";
  } else if (fetchComplete && (isTreeBuilding || treeComplete)) {
    fetchStatus = "complete";
  } else if (isFetching || !isTreeBuilding) {
    // Still fetching OR waiting for tree to start (data propagating through React)
    fetchStatus = "loading";
  } else {
    fetchStatus = "pending";
  }

  tasks.push({
    id: "fetch",
    label: fetchLabel,
    status: fetchStatus,
    progress: fetchProgress,
    progressLabel: fetchProgressLabel,
  });

  // Phase 2: Building tree structure in worker
  let treeStatus: LoadingTask["status"];
  if (treeComplete) {
    treeStatus = "complete";
  } else if (isTreeBuilding) {
    treeStatus = "loading";
  } else {
    treeStatus = "pending";
  }

  // Build tree task with progress
  let treeLabel = i18n.t("loading-overlay.buildingTree");
  let treeProgress: number | undefined;
  let treeProgressLabel: string | undefined;

  if (isTreeBuilding && treeBuildProgress) {
    const { phase, percent, processedCount, totalCount } = treeBuildProgress;

    // Set label based on phase
    switch (phase) {
      case "loading":
        treeLabel = i18n.t("loading-overlay.tree.loading");
        break;
      case "deserializing":
        treeLabel = i18n.t("loading-overlay.tree.deserializing");
        break;
      case "grouping":
        treeLabel = i18n.t("loading-overlay.tree.grouping");
        break;
      case "flattening":
        treeLabel = i18n.t("loading-overlay.tree.flattening");
        break;
    }

    // Set progress
    treeProgress = percent;

    // Show progress label for flattening phase (has meaningful counts)
    if (phase === "flattening" && totalCount > 0) {
      treeProgressLabel = `${processedCount.toLocaleString()} / ${totalCount.toLocaleString()} rows`;
    }
  }

  tasks.push({
    id: "tree",
    label: treeLabel,
    status: treeStatus,
    progress: treeProgress,
    progressLabel: treeProgressLabel,
  });

  // Phase 3 (optional): Loading additional metadata
  if (isMetadataLoading || metadataComplete) {
    tasks.push({
      id: "metadata",
      label: metadataLabel ?? i18n.t("loading-overlay.fetchingMetadata"),
      status: metadataComplete ? "complete" : isMetadataLoading ? "loading" : "pending",
    });
  }

  return tasks;
};

// Animations
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/**
 * Minimal step indicator - just dots
 */
const StepIndicator = ({ tasks }: { tasks: LoadingTask[] }) => {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {tasks.map((task) => {
        const isComplete = task.status === "complete";
        const isCurrent = task.status === "loading";

        return (
          <Box
            key={task.id}
            sx={{
              width: isCurrent ? 20 : 6,
              height: 6,
              borderRadius: 3,
              backgroundColor: isComplete ? theme.palette.primary.main : isCurrent ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.15),
              transition: "all 0.3s ease",
              ...(isCurrent && {
                animation: `${pulse} 1.5s ease-in-out infinite`,
              }),
            }}
          />
        );
      })}
    </Stack>
  );
};

/**
 * Custom DataGrid loading overlay component.
 *
 * @see https://v7.mui.com/x/react-data-grid/overlays/#custom-component
 */
export const LoadingOverlayComponent = (props: LoadingOverlayProps) => {
  const { tasks = getDefaultSingleModelTasks(), title, customVariant = "tasks" } = props;
  const theme = useTheme();
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const dm = appStore.downloadManager;
  const dmState = dm.state;

  // Grid API not required for simplified overlay; skip row count checks

  // Find the current active task
  const activeTask = tasks.find((t) => t.status === "loading");

  // Get selected model IDs for per-model status display
  const scenarioId = selectionManager.selectedScenarioId;
  const allSelectedModelIds = scenarioId ? selectionManager.getSelectedModelIds(scenarioId) : [];
  const scenarioModelIds = new Set(selectionManager.selectedScenario?.scenarioModels?.map((sm: any) => sm.modelId).filter(Boolean) || []);
  const selectedModelIds = allSelectedModelIds.filter((modelId: string) => scenarioModelIds.has(modelId));

  // Hide overlay only when the tree task is specifically complete
  // Don't hide if tree task is pending (waiting to start) or loading
  // Also verify there's no active task (edge case during state transitions)
  const treeTask = tasks.find((t) => t.id === "tree");
  const isTreeComplete = treeTask?.status === "complete";
  const allTasksComplete = tasks.length > 0 && tasks.every((t) => t.status === "complete");

  // Only hide if truly complete: all tasks done, tree specifically done, metadata done, AND no active task
  const metadataTask = tasks.find((t) => t.id === "metadata");
  const isMetadataComplete = !metadataTask || metadataTask.status === "complete";

  if (allTasksComplete && isTreeComplete && isMetadataComplete && !activeTask) {
    console.log("[LoadingOverlay] All tasks complete, hiding overlay");
    return null;
  }

  // Use the active task's label directly
  const displayLabel = activeTask?.label || "Loading...";
  console.log("[LoadingOverlay]", displayLabel, { activeTask, tasks });

  // Minimal variant - just text and dots at the top
  if (customVariant === "minimal") {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            ${theme.palette.primary.main} 50%, 
            transparent 100%)`,
          backgroundSize: "200% 100%",
          animation: `${shimmer} 1.5s ease-in-out infinite`,
        }}
      />
    );
  }

  // Linear variant - progress bar at top
  if (customVariant === "linear") {
    return (
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0 }}>
        <LinearProgress
          sx={{
            height: 2,
            "& .MuiLinearProgress-bar": {
              transition: "none",
            },
          }}
        />
      </Box>
    );
  }

  // For simplified UX, hide determinate progress and bytes
  const hasProgress = false;
  const progressValue = 0;
  const progressLabel = undefined;

  // Tasks variant - centered, clean display
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        height: "100%",
        backgroundColor: alpha(theme.palette.background.default, 0.9),
        backdropFilter: "blur(4px)",
      }}
    >
      <Stack alignItems="center" spacing={2} sx={{ minWidth: 200 }}>
        {/* Progress indicator - either determinate bar or animated shimmer */}
        {hasProgress ? (
          <Box sx={{ width: "100%", maxWidth: 280 }}>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  transition: "transform 0.2s ease",
                },
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: 48,
              height: 3,
              borderRadius: 1.5,
              background: `linear-gradient(90deg, 
                ${alpha(theme.palette.primary.main, 0.3)} 0%, 
                ${theme.palette.primary.main} 50%, 
                ${alpha(theme.palette.primary.main, 0.3)} 100%)`,
              backgroundSize: "200% 100%",
              animation: `${shimmer} 1.2s ease-in-out infinite`,
            }}
          />
        )}

        {/* Status text */}
        <Stack alignItems="center" spacing={0.5}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            {displayLabel}
          </Typography>

          {/* Progress details (bytes downloaded, percentage, etc.) */}
          {hasProgress && (
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.text.secondary, 0.7),
                fontWeight: 400,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {progressLabel || `${progressValue}%`}
            </Typography>
          )}
        </Stack>

        {/* Compact per-model status for multi-selection */}
        {dmState.total > 0 && (
          <Stack spacing={0.25} sx={{ mt: 0.5, width: "100%", maxWidth: 420 }}>
            {/* Sort models to prioritize active downloads over completed ones */}
            {[...selectedModelIds]
              .sort((a, b) => {
                const statusA = (dmState.perModel || {})[a];
                const statusB = (dmState.perModel || {})[b];
                // Priority: downloading/checking/writing > queued > error > completed/cached
                const getPriority = (s: any) => {
                  if (!s) return 5;
                  if (s.status === MODEL_TRANSFER_STATUS.DOWNLOADING) return 1; // Active
                  if (s.status === MODEL_TRANSFER_STATUS.QUEUED) return 2; // Waiting
                  if (s.status === MODEL_TRANSFER_STATUS.ERROR) return 3; // Error (should be visible)
                  if (s.status === MODEL_TRANSFER_STATUS.COMPLETED) return 4; // Done
                  return 5;
                };
                return getPriority(statusA) - getPriority(statusB);
              })
              .slice(0, 3)
              .map((modelId) => {
                const s = (dmState.perModel || {})[modelId];
                if (!s) return null;
                const model = selectionManager.selectedProject?.models?.find((m: any) => m.modelId === modelId);
                const label = model?.apsItemName || model?.modelName || modelId;

                const statusText = (() => {
                  // Debug: log the actual status values
                  if (s.status === MODEL_TRANSFER_STATUS.ERROR) {
                    console.log("[LoadingOverlay] Model error state:", { modelId, label, status: s.status, phase: s.phase, errorCode: s.errorCode });
                  }

                  // Terminal statuses
                  if (s.status === MODEL_TRANSFER_STATUS.ERROR) {
                    return i18n.t("loading-overlay.download.error");
                  }
                  if (s.status === MODEL_TRANSFER_STATUS.CANCELED) {
                    return i18n.t("loading-overlay.download.canceled");
                  }

                  // Phases
                  switch (s.phase) {
                    case MODEL_TRANSFER_PHASE.CHECKING:
                      return i18n.t("loading-overlay.download.checking");
                    case MODEL_TRANSFER_PHASE.DOWNLOADING: {
                      const pct = typeof s.percent === "number" ? i18n.t("loading-overlay.download.percent", { percent: Math.round(s.percent) }) : "";
                      return i18n.t("loading-overlay.download.downloading", { percent: pct });
                    }
                    case MODEL_TRANSFER_PHASE.WRITING:
                      return i18n.t("loading-overlay.download.writing");
                    case MODEL_TRANSFER_PHASE.CACHED:
                      return i18n.t("loading-overlay.download.cached");
                    case MODEL_TRANSFER_PHASE.COMPLETE:
                      return i18n.t("loading-overlay.download.complete");
                    default:
                      return i18n.t("loading-overlay.download.working");
                  }
                })();

                return (
                  <Typography key={modelId} variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.8) }}>
                    {label}: {statusText}
                  </Typography>
                );
              })
              .filter(Boolean)}
            {selectedModelIds.length > 3 && (
              <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.6) }}>
                +{selectedModelIds.length - 3} more...
              </Typography>
            )}
          </Stack>
        )}

        {/* Step dots */}
        <StepIndicator tasks={tasks} />
      </Stack>
    </Stack>
  );
};

export const LoadingOverlay = observer(LoadingOverlayComponent);
export default LoadingOverlay;

/**
 * Props for the NoRowsOverlay component.
 * Extends HTMLAttributes to be compatible with MUI DataGrid slots.
 */
export interface NoRowsOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Whether data is currently being loaded */
  isLoading?: boolean;
  /** Loading tasks to display when loading */
  tasks?: LoadingTask[];
  /** Message to show when there's truly no data */
  noDataMessage?: string;
}

/**
 * Custom DataGrid noRowsOverlay that shows loading progress when data is being fetched,
 * and "No rows" message only when there's truly no data.
 */
export const NoRowsOverlay = (props: NoRowsOverlayProps) => {
  const {
    isLoading = false,
    tasks = getDefaultSingleModelTasks(),
    noDataMessage = i18n.t("no-rows-overlay.noRows"),
    // Extract HTML div props to avoid passing them to child components
    className,
    style,
    ...rest
  } = props;
  const theme = useTheme();

  // If we're loading, show the loading overlay instead of "No rows"
  if (isLoading) {
    return (
      <Box className={className} style={style} sx={{ height: "100%", width: "100%" }} {...rest}>
        <LoadingOverlay tasks={tasks} customVariant="tasks" />
      </Box>
    );
  }

  // Not loading - show "No rows" message
  return (
    <Stack
      className={className}
      style={style}
      alignItems="center"
      justifyContent="center"
      sx={{
        height: "100%",
        backgroundColor: alpha(theme.palette.background.default, 0.9),
      }}
      {...rest}
    >
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          fontWeight: 500,
        }}
      >
        {noDataMessage || i18n.t("no-rows-overlay.noRows")}
      </Typography>
    </Stack>
  );
};
